import $ from 'jquery';
import KnockoutElse from 'knockout-else/dist/knockout-else';
import { browsersRegex, browsersRegexes } from 'virtual:supported-browsers';

import { enableBrowserSupportCheck, isCiTestEnv } from './global/flags';
import isGoodxwebChildWindow from './util/isGoodxwebChildWindow';

import './bootstrap-globals';
import 'jquery-ui';
import 'jquery-ui.autocomplete.scroll';
import './lib/external/jquery.resize';
import './lib/moment-extra';
import './lib/luxon-extra';
import './lib/knockout-extra/util';
import './lib/knockout-extra/extenders';
import './lib/knockout-extra/types';
import 'mousetrap/plugins/record/mousetrap-record';

KnockoutElse.init();

// Do some CI-specific stuff.
if (isCiTestEnv) {
    // Disable animations to reduce cpu usage.
    const disable_animations = window.document.createElement('style');
    disable_animations.textContent = `
            * {
                transition-property: none !important;
                animation: none !important;
            }
        `;
    window.document.head.append(disable_animations);
}

// Set the pixel density for iPads.
if (window.navigator.platform === 'iPad') {
    $('meta[name="viewport"]').attr(
        'content',
        'width=device-width, initial-scale=0.8, maximum-scale=0.8, minimum-scale=0.8, user-scalable=yes',
    );
}

$(async () => {
    // Don't load the web if we're not at the base path.
    // This is mostly used for subapps.
    if (window.location.pathname.length > 1) {
        return;
    }

    // Check that the browser is supported.
    if (!browsersRegex.test(window.navigator.userAgent) && enableBrowserSupportCheck) {
        const display_names = browsersRegexes.map(r => r.family + ' ' + r.requestVersions[0]?.[0]).join(', ');
        error(`
            <h2>Unsupported browser detected.</h2>
            The minimum supported browser versions are:<br>
            ${display_names}.
        `);
        throw new Error('Unsupported browser detected');
    }

    // Detect if goodxweb is used in multiple tabs.
    let multiple = false;
    function checkNotMultipleTabs() {
        if (!multiple) {
            return;
        }
        error(`
                <h2>Multiple tabs detected.</h2>
                You appear to be running this site in multiple tabs which is not supported.<br>
                Please close this tab and use the one that is already open.
            `);
        throw new Error('Multiple tabs detected');
    }

    // Multiple tab detection on apple devices does not currently work
    // because the storage event is not spec-compliant.
    const isIOS =
        /iPad|iPhone|iPod/.test(window.navigator.platform) ||
        window.navigator.platform === 'MacIntel' ||
        window.navigator.maxTouchPoints > 1;
    if (!isIOS) {
        detectMultipleTabs(() => (multiple = true));
    }

    // Register the service worker.
    if ('serviceWorker' in window.navigator) {
        const path = import.meta.env.DEV ? 'src/serviceworker/bootstrap.ts' : '/sw.js';
        window.navigator.serviceWorker.register(path, { type: 'module', scope: '/' }).then(
            registration => {
                window.console.log('ServiceWorker registration successful with scope:', registration.scope);
            },
            (err: unknown) => {
                window.console.log('ServiceWorker registration failed:', err);
            },
        );
    }

    const main = await import('./main');
    if (!isGoodxwebChildWindow()) {
        checkNotMultipleTabs();
    }
    await main.start();

    // Warn before leaving the page (back button/outgoing link).
    // If all open screens can close without user confirmation (no state loss), don't warn.
    const { default: window_manager } = await import('./screens/WindowManager');
    $(window).on('beforeunload', e => {
        if (window_manager.stack().some(entry => !entry.screen._close())) {
            e.preventDefault();
        }
    });
});

// Display a fullscreen error screen with the specified message.
function error(msg: string) {
    $('.LoadScreen .animation').hide();
    $('.LoadScreen .body').css('background', '#d44949');
    $('.LoadScreen .status').html(msg);
    $(window).off('beforeunload');
}

// Detect the presence of multiple tabs and invoke a callback when detected.
function detectMultipleTabs(callback: () => void) {
    const key = 'tabid';
    const id = (Number.parseInt(window.localStorage.getItem(key) || '', 10) || 0) + 1;
    const handler = (e: StorageEvent) => {
        if (e.key === key) {
            const newid = Number.parseInt(e.newValue || '', 10) || Infinity;
            if (id < newid) {
                window.localStorage.setItem(key, id.toString());
            } else {
                window.removeEventListener('storage', handler);
                callback();
            }
        }
    };
    window.addEventListener('storage', handler);
    window.localStorage.setItem(key, id.toString());
}

if (import.meta.hot) {
    // Disable prompt before reloading.
    import.meta.hot.on('vite:beforeFullReload', () => {
        $(window).off('beforeunload');
    });
}
